import React from "react"
import { makeStyles } from "tss-react/mui"
import { Box, DialogContent, Grid, Typography } from "@mui/material"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { StandardDialog } from "../StandardDialog"

type StyleProps = Readonly<{
    isImage?: boolean
}>

const useStyles = makeStyles<StyleProps>()((theme, { isImage = false }) => ({
    dialogPaper: {
        maxWidth: "50rem",
    },
    dialogContent: {
        padding: "2rem 4rem 4rem",
        display: "flex",
        flexWrap: "nowrap",
        flexDirection: "row",
        gap: "1rem",
        color: theme.palette.tailwind.slate[900],
    },
    list: {
        display: "flex",
        flexDirection: isImage ? "column" : "row",
        gap: "2rem",
        flexWrap: "wrap",
        "& > div": {
            width: isImage ? "100%" : "46%",
        },
    },
    details: {
        color: theme.palette.tailwind.slate[700],
    },
    detailsListWrap: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
        flexWrap: "wrap",
    },
    imageWrap: {
        width: "100%",
    },
    image: {
        width: "100%",
        height: "auto",
        objectFit: "contain",
    },

    dot: {
        flex: "none",
        display: "block",
        width: ".5rem",
        height: ".5rem",
        borderRadius: "50%",
        backgroundColor: theme.palette.tailwind.slate[500],
    },
}))

type ListItem = Readonly<{
    name: string
}>

type ArrangementListProps = Readonly<{
    items: ListItem[]
}>

export function ArrangementList({ items }: ArrangementListProps) {
    const { classes } = useStyles({})

    return (
        <Box className={classes.detailsListWrap}>
            {items.map((item, index) => (
                <React.Fragment key={item.name}>
                    <Typography variant="body1" className={classes.details}>
                        {item.name}
                    </Typography>
                    {index < items.length - 1 && <span className={classes.dot} />}
                </React.Fragment>
            ))}
        </Box>
    )
}

export type ResourceDetailsDialogParams = Readonly<{
    title: string
    subTitle?: string
    location?: string
    description?: string
    seats?: number
    floor?: number
    squareMeters?: number
    walkDirection?: string
    tableSetups?: ListItem[]
    avEquipments?: ListItem[]
    image?: {
        url: string
    }
    meetingUsage?: {
        internal: boolean
        external: boolean
    }
}>

type ResourceDetailsDialogProps = Readonly<{
    resourceDetails: ResourceDetailsDialogParams
    handleClose: () => void
}>

export function ResourceDetailsDialog({ resourceDetails, handleClose }: ResourceDetailsDialogProps) {
    const {
        title,
        subTitle,
        location,
        description,
        floor,
        squareMeters,
        seats,
        walkDirection,
        tableSetups,
        avEquipments,
        image,
        meetingUsage,
    } = resourceDetails ?? {}

    const { classes, cx } = useStyles({ isImage: !!image?.url })

    return (
        <StandardDialog
            open={!!resourceDetails}
            onClose={handleClose}
            dialogTitle={title}
            subtitle={subTitle}
            slotProps={{
                paper: { className: classes.dialogPaper },
            }}
            maxWidthProp="md"
        >
            <DialogContent className={classes.dialogContent}>
                <Grid item xs={!!image?.url ? 6 : 12} className={classes.list}>
                    {description && (
                        <Box>
                            <LocalizedStrict id="resource-details-dialog-description">
                                <Typography variant="subtitle1" fontWeight={700}>
                                    Description
                                </Typography>
                            </LocalizedStrict>
                            <Typography variant="body1" className={classes.details}>
                                {description}
                            </Typography>
                        </Box>
                    )}
                    {location && (
                        <Box>
                            <LocalizedStrict id="resource-details-dialog-service-center">
                                <Typography variant="subtitle1" fontWeight={700}>
                                    Service center
                                </Typography>
                            </LocalizedStrict>

                            <Typography variant="body1" className={classes.details}>
                                {location}
                            </Typography>
                        </Box>
                    )}
                    {Number.isFinite(floor) && (
                        <Box>
                            <LocalizedStrict id="resource-details-dialog-floor">
                                <Typography variant="subtitle1" fontWeight={700}>
                                    Floor
                                </Typography>
                            </LocalizedStrict>

                            <Typography variant="body1" className={classes.details}>
                                {floor}
                            </Typography>
                        </Box>
                    )}

                    {Number.isFinite(squareMeters) && (
                        <Box>
                            <LocalizedStrict id="resource-details-dialog-square-meters">
                                <Typography variant="subtitle1" fontWeight={700}>
                                    Square meters
                                </Typography>
                            </LocalizedStrict>

                            <Typography variant="body1" className={classes.details}>
                                {squareMeters}
                            </Typography>
                        </Box>
                    )}

                    {walkDirection && (
                        <Box>
                            <LocalizedStrict id="resource-details-dialog-walk-direction">
                                <Typography variant="subtitle1" fontWeight={700}>
                                    Walk direction
                                </Typography>
                            </LocalizedStrict>

                            <Typography variant="body1" className={classes.details}>
                                {walkDirection}
                            </Typography>
                        </Box>
                    )}

                    <Box>
                        <LocalizedStrict id="resource-details-dialog-reserved-for">
                            <Typography variant="subtitle1" fontWeight={700}>
                                Reserved for
                            </Typography>
                        </LocalizedStrict>

                        <Typography variant="body1" className={cx(classes.details, classes.detailsListWrap)}>
                            {(() => {
                                const showInternal = !meetingUsage || meetingUsage.internal
                                const showExternal = !meetingUsage || meetingUsage.external

                                const items = []
                                if (showInternal) {
                                    items.push(
                                        <LocalizedStrict
                                            key="internal"
                                            id="resource-details-dialog-reserved-for-internal"
                                        >
                                            <>Internal meetings</>
                                        </LocalizedStrict>,
                                    )
                                }
                                if (showInternal && showExternal) {
                                    items.push(<span key="dot" className={classes.dot} />)
                                }
                                if (showExternal) {
                                    items.push(
                                        <LocalizedStrict
                                            key="external"
                                            id="resource-details-dialog-reserved-for-external"
                                        >
                                            <>External meetings</>
                                        </LocalizedStrict>,
                                    )
                                }

                                return items
                            })()}
                        </Typography>
                    </Box>
                    {Number.isFinite(seats) && (
                        <Box>
                            <LocalizedStrict id="resource-details-dialog-capacity">
                                <Typography variant="subtitle1" fontWeight={700}>
                                    Capacity
                                </Typography>
                            </LocalizedStrict>
                            <LocalizedStrict
                                id="resource-details-dialog-capacity-quantity"
                                vars={{ quantity: seats ?? 0 }}
                            >
                                <Typography variant="body1" className={classes.details}>
                                    {seats}
                                </Typography>
                            </LocalizedStrict>
                        </Box>
                    )}
                    {!!tableSetups?.length && (
                        <Box>
                            <LocalizedStrict id="resource-details-dialog-table-arrangement">
                                <Typography variant="subtitle1" fontWeight={700}>
                                    Table arrangement
                                </Typography>
                            </LocalizedStrict>
                            <ArrangementList items={tableSetups} />
                        </Box>
                    )}
                    {!!avEquipments?.length && (
                        <Box>
                            <LocalizedStrict id="resource-details-dialog-av-equipment">
                                <Typography variant="subtitle1" fontWeight={700}>
                                    AV-equipment
                                </Typography>
                            </LocalizedStrict>
                            <ArrangementList items={avEquipments} />
                        </Box>
                    )}
                </Grid>
                {image?.url && (
                    <Grid item xs={6} className={classes.imageWrap}>
                        <img src={image.url} className={classes.image} />
                    </Grid>
                )}
            </DialogContent>
        </StandardDialog>
    )
}
